import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid-pro'
import { DataGrid, useDataGridProps } from '@cbgms/components/list/DataGrid'
import { useListService } from '@cbgms/components/list/use-list-service/use-list-service'
import { useDataGridActions } from '@cbgms/components/list/use-data-grid-actions'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Stack } from '@mui/material'
import { AddConceptConfigurationButton } from './AddConceptConfigurationButton'
import { ConceptConfigurationResponse } from '@cbgms/api/modules/concept-configuration'
import { DataGridHeader } from '@cbgms/components/list/DataGridHeader'

const ConceptConfigurationList: React.FC = () => {
  const { t } = useTranslation()
  const match = useRouteMatch()
  const history = useHistory()

  const onEdit = React.useCallback(
    (row: ConceptConfigurationResponse) => {
      history.push(`${match.url}/edit/${row.ConceptConfiguration.Application}`)
    },
    [match.url]
  )

  const rowActions = (params: GridRowParams<ConceptConfigurationResponse>) => {
    return [<GridActionsCellItem icon={<EditIcon />} label={t('global.edit')} onClick={() => onEdit(params.row)} />]
  }

  const handleDoubleClick = (params: GridRowParams<ConceptConfigurationResponse>) => onEdit(params.row)

  const listService = useListService<ConceptConfigurationResponse>({
    application: 'support',
    module: 'concept-configuration/list',
    setting: 'concept-configuration/list'
  })

  const dataGridProps = useDataGridProps<ConceptConfigurationResponse>({
    key: 'concept-configuration',
    ...listService,
    getRowId: row => row.ConceptConfiguration.Application
  })

  const dataGridActionProps = useDataGridActions(dataGridProps.columns, dataGridProps.pinnedColumns, rowActions)

  dataGridActionProps.columns = useMemo(() => {
    return dataGridActionProps.columns.map(column => {
      if (column.field === 'ConceptConfiguration.Facilities') {
        return {
          ...column,
          renderCell: params =>
            params.row.ConceptConfiguration.Facilities !== null && params.row.ConceptConfiguration.Facilities.length > 0
              ? params.row.ConceptConfiguration.Facilities.map(facility => t(`Enum.EnumShopFacilities_${facility}`)).join(', ')
              : ''
        }
      } else if (column.field === 'ConceptConfiguration.Services') {
        return {
          ...column,
          renderCell: params =>
            params.row.ConceptConfiguration.Services !== null && params.row.ConceptConfiguration.Services.length > 0
              ? params.row.ConceptConfiguration.Services.map(service => t(`Enum.EnumShopServices_${service}`)).join(', ')
              : ''
        }
      } else if (column.field === 'ConceptConfiguration.PaymentMethods') {
        return {
          ...column,
          renderCell: params =>
            params.row.ConceptConfiguration.PaymentMethods !== null && params.row.ConceptConfiguration.PaymentMethods.length > 0
              ? params.row.ConceptConfiguration.PaymentMethods.map(pm => {
                  return t(`Enum.EnumShopPaymentMethods_${pm}`)
                }).join(', ')
              : ''
        }
      } else if (column.field !== 'actions') {
        return {
          ...column,
          renderHeader: () => <DataGridHeader field={column.field} />
        }
      }
      return column
    })
  }, [dataGridActionProps.columns])

  return (
    <Stack>
      <Box mb={1}>
        <AddConceptConfigurationButton />
      </Box>
      <DataGrid
        {...dataGridProps}
        {...dataGridActionProps}
        slots={{ toolbar: null }}
        onRowDoubleClick={handleDoubleClick}
        disableColumnMenu
      />
    </Stack>
  )
}

export default ConceptConfigurationList
